/**
 * FieldError — shared types, Zod prop schema and styling constants.
 *
 * One error row (alert icon + message) shown under a field or a group of
 * controls. A field or group references the row's `id` from its
 * `aria-describedby`, so the message is read as part of the control.
 *
 * Source: Figma file hk9NaJrKRwkoGmmZ5rxbsE, page v7, "Web/Field error"
 * (node 99:7462). The v7 sheet was drawn from this package's code rather than
 * the UI Design System library, so its variable names stand until design adds
 * them to the library.
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { typography } from '../../elements/typography';
import type { TypeStyle } from '../../elements/typography';
import type { IconName } from '../../elements/icons/registry';

/** Catalog-facing schema: data props only. The message is caller-supplied copy. */
export const fieldErrorPropsSchema = z.object({
  /** The error sentence. Empty or omitted renders nothing. */
  message: z.string().optional(),
  /** DOM id a field or group points its `aria-describedby` at. */
  id: z.string().optional(),
  testID: z.string().optional(),
});

export type FieldErrorProps = z.infer<typeof fieldErrorPropsSchema>;

export const FIELD_ERROR_ICON: IconName = 'alert';
export const FIELD_ERROR_ICON_SIZE = sizing.icon.xs;
// v7 binds the icon to `icon/alert`, which has no code variant of its own;
// `text.alert` holds the same colour, so it stands in until one is added.
export const FIELD_ERROR_ICON_COLOR = colors.text.alert;
export const FIELD_ERROR_TEXT_COLOR = colors.text.alert;
export const FIELD_ERROR_TYPOGRAPHY: TypeStyle = typography.c1Medium;
export const FIELD_ERROR_GAP = spacing.s;

/** True when there is a message worth rendering (not empty or whitespace). */
export function hasFieldErrorMessage(message: string | undefined): message is string {
  return typeof message === 'string' && message.trim() !== '';
}
